import { useState, useEffect } from 'react';
import { setPaid } from '../store/authSlice';
import { kisApproval, kisConnect, kisDisconnect } from '../utils/kisAgent.ts';
import { makeKisPaymentSpec } from '../utils/kisPayment.ts';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation } from './useNavigation.ts';
import { RootState } from '../store/rootReducer.ts';
import { incrementFailureCount, resetFailureCount } from '../store/paymentSlice.ts';

interface UsePaymentProps {
  amount: number;
}

export function usePayment({ amount }: UsePaymentProps) {
  const [isCardPayment, setIsCardPayment] = useState(false);
  const [isPaymentCompleted, setIsPaymentCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isErrorHandlingInProgress, setIsErrorHandlingInProgress] = useState(false);

  const failureCount = useSelector((state: RootState) => state.payment.failureCount);
  const dispatch = useDispatch();
  const { navigateToStart, navigateToStandby, navigateToCopy } = useNavigation();

  const handlePaymentFailure = (errorMessage: string) => {
    if (isPaymentCompleted || isErrorHandlingInProgress) return;

    setIsErrorHandlingInProgress(true);
    dispatch(incrementFailureCount());

    if (failureCount + 1 >= 3) {
      setError('결제 시도가 3회 이상 실패했습니다. 처음부터 다시 시도해주세요.');
      setTimeout(() => {
        setIsErrorHandlingInProgress(false);
        dispatch(resetFailureCount());
        navigateToStart();
      }, 5000);
    } else {
      setError(errorMessage);
      setTimeout(() => {
        setIsErrorHandlingInProgress(false);
        navigateToCopy();
      }, 5000);
    }
  };

  const handlePaymentSuccess = () => {
    setIsPaymentCompleted(true);
    dispatch(setPaid(true));

    setTimeout(() => {
      kisDisconnect();
    }, 1000);

    setTimeout(() => {
      navigateToStandby();
    }, 10000);
  };

  const requestKisPayment = () => {
    console.log('KIS 결제 준비...');
    let hasErrorOccurred = false;
    let hasSuccessOccurred = false;

    kisConnect('payment', 'ws', {
      onError: () => {
        if (!hasErrorOccurred && !hasSuccessOccurred) {
          hasErrorOccurred = true;
          handlePaymentFailure('결제 단말기 연결에 실패했습니다.');
        }
      },
      onClose: () => {
        if (!hasErrorOccurred && !hasSuccessOccurred) {
          hasErrorOccurred = true;
          handlePaymentFailure('결제 단말기 연결이 종료되었거나 실패했습니다. 다시 시도해주세요.');
        }
      },
      onOpen: () => {
        setTimeout(() => {
          try {
            const message = makeKisPaymentSpec(amount);
            console.log('Sending payment request: ', message);
            kisApproval(message);
          } catch (e) {
            if (!hasErrorOccurred && !hasSuccessOccurred) {
              hasErrorOccurred = true;
              handlePaymentFailure(`결제 요청 전송에 실패했습니다: ${e.message}`);
            }
          }
        }, 1000);
      },
      onMessage: (data) => {
        const response = JSON.parse(data);
        if (response.outReplyCode && response.outReplyCode !== '0000') {
          if (!hasErrorOccurred && !hasSuccessOccurred) {
            hasErrorOccurred = true;
            handlePaymentFailure(
              `결제 요청 전송에 실패했습니다 (ErrorCode ${response.outAgentCode}): ${response.outReplyMsg1}`
            );
          }
        } else {
          hasSuccessOccurred = true;
          handlePaymentSuccess();
        }
      },
    });

    setIsCardPayment(true);
  };

  const handleError = () => {
    setIsCardPayment(false);
    setError(null);
  };

  const handleTestApproval = () => {
    if (!import.meta.env.DEV) return;

    setIsCardPayment(true);
    setIsPaymentCompleted(true);
    dispatch(resetFailureCount());
    dispatch(setPaid(true));

    setTimeout(() => {
      navigateToStandby();
    }, 10000);
  };

  useEffect(() => {
    return () => {
      kisDisconnect();
    };
  }, []);

  return {
    isCardPayment,
    isPaymentCompleted,
    error,
    handleError,
    requestKisPayment,
    failureCount,
    handleTestApproval,
  };
}
